import { useId, useState, type FormEvent } from 'react';
import { useOne, useCreate, useUpdate, useList, useGo } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { Loader2, Link2, Copy } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FormHeader, FormStickyActions, FormSpacer } from '@/components/forms';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { notify } from '@/lib/notify';
import { LinkTargetSelect } from '@/components/shared';
import type { UtmSourceItem } from './types';
import type { TagItem } from '../tags/types';
import { TagPickerWithCreate } from '../tags/components';

interface UtmSourceFormData {
  displayName: string;
  name: string;
  autoTagId: number | null;
  redirectUrl: string | null;
}

function buildInitialState(source?: UtmSourceItem): UtmSourceFormData {
  if (!source) {
    return { displayName: '', name: '', autoTagId: null, redirectUrl: null };
  }
  return {
    displayName: source.displayName,
    name: source.name,
    autoTagId: source.autoTagId,
    redirectUrl: source.redirectUrl ?? null,
  };
}

export function UtmSourcesForm() {
  const { id } = useParams<{ id: string }>();
  const go = useGo();
  const isEdit = !!id;

  const oneResult = useOne<UtmSourceItem>({
    resource: 'utm-sources',
    id: id ? parseInt(id, 10) : undefined,
    queryOptions: { enabled: isEdit },
  });

  const sourceData = oneResult.query.data?.data;
  const isLoadingData = oneResult.query.isLoading;

  if (isEdit && isLoadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const formKey = isEdit ? `edit-${sourceData?.id ?? 'loading'}` : 'create';

  return (
    <UtmSourcesFormFields
      key={formKey}
      isEdit={isEdit}
      id={id}
      initialState={buildInitialState(sourceData)}
      shortCode={sourceData?.shortCode}
      onBack={() => go({ to: '/settings/utm-sources' })}
    />
  );
}

interface UtmSourcesFormFieldsProps {
  isEdit: boolean;
  id?: string;
  initialState: UtmSourceFormData;
  shortCode?: string;
  botLink?: string | null;
  onBack: () => void;
  layout?: 'page' | 'dialog';
  onSuccess?: (source: UtmSourceItem) => void;
}

export function UtmSourcesFormFields({
  isEdit,
  id,
  initialState,
  shortCode,
  botLink,
  onBack,
  layout = 'page',
  onSuccess,
}: UtmSourcesFormFieldsProps) {
  const go = useGo();
  const formId = useId();
  const displayNameId = `${formId}-display-name`;
  const nameId = `${formId}-name`;
  const [formData, setFormData] = useState<UtmSourceFormData>(initialState);
  const [savedShortCode, setSavedShortCode] = useState<string | undefined>(shortCode);
  const [savedBotLink, setSavedBotLink] = useState<string | null | undefined>(botLink);
  const isPageLayout = layout === 'page';

  const createMutation = useCreate<UtmSourceItem>();
  const updateMutation = useUpdate<UtmSourceItem>();

  const isSubmitting = createMutation.mutation.isPending || updateMutation.mutation.isPending;

  // Загрузка тегов для select
  const tagsResult = useList<TagItem>({
    resource: 'tags',
    pagination: { pageSize: 200, mode: 'server' },
  });
  const tags = tagsResult.query.data?.data ?? [];
  const activeTags = tags.filter((t) => t.deletedAt === null);

  const selectedTag = activeTags.find((t) => t.id === formData.autoTagId) ?? null;

  const handleChange = <K extends keyof UtmSourceFormData>(field: K, value: UtmSourceFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): boolean => {
    if (!formData.displayName.trim()) {
      notify.error('Введите отображаемое название');
      return false;
    }
    if (!isEdit && !formData.name.trim()) {
      notify.error('Введите код источника (name)');
      return false;
    }
    if (!isEdit && !/^[A-Za-z0-9_]+$/.test(formData.name.trim())) {
      notify.error('Код источника может содержать только латинские буквы, цифры и подчёркивание');
      return false;
    }
    return true;
  };

  const submitData = () => {
    if (!validate()) { return; }

    const values = {
      displayName: formData.displayName.trim(),
      ...(isEdit ? {} : { name: formData.name.trim() }),
      autoTagId: formData.autoTagId,
      redirectUrl: formData.redirectUrl,
    };

    if (isEdit && id) {
      updateMutation.mutate(
        { resource: 'utm-sources', id: parseInt(id, 10), values },
        {
          onSuccess: (result) => {
            onSuccess?.(result.data);
            if (!onSuccess) {
              go({ to: '/settings/utm-sources' });
            }
          },
          onError: (error) => {
            const message = error.message ?? 'Ошибка обновления';
            notify.error(message);
          },
        }
      );
    } else {
      createMutation.mutate(
        { resource: 'utm-sources', values },
        {
          onSuccess: (result) => {
            const created = result.data;
            setSavedShortCode(created.shortCode);
            setSavedBotLink(created.botLink);
            onSuccess?.(created);
          },
          onError: (error) => {
            const message = error.message ?? 'Ошибка создания';
            notify.error(message);
          },
        }
      );
    }
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitData();
  };

  const handleCopyLink = () => {
    const link = savedBotLink ?? savedShortCode;
    if (!link) { return; }
    navigator.clipboard.writeText(link).catch(() => {
      notify.error('Не удалось скопировать');
    });
    notify.success('Ссылка скопирована');
  };

  const fields = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={displayNameId}>Отображаемое название *</Label>
        <Input
          id={displayNameId}
          placeholder="Instagram Реклама"
          value={formData.displayName}
          onChange={(e) => handleChange('displayName', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={nameId}>
          Код источника (name) *
          {isEdit && <span className="ml-2 text-xs text-muted-foreground">(нельзя изменить)</span>}
        </Label>
        <Input
          id={nameId}
          placeholder="instagram_ads"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          disabled={isEdit}
          className={cn('font-mono', isEdit && 'opacity-60')}
        />
        {!isEdit && (
          <p className="text-xs text-muted-foreground">
            Только латинские буквы, цифры и подчёркивание. После создания изменить нельзя.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Авто-тег</Label>
        <TagPickerWithCreate
          tags={activeTags}
          value={formData.autoTagId}
          onChange={(tagId) => handleChange('autoTagId', tagId)}
          placeholder="Не выбрано"
          clearLabel="Не выбрано"
          createDialogTitle="Создать авто-тег"
          createDialogDescription="Новый тег сразу будет выбран для этой UTM ссылки."
          createSuccessMessage="Тег создан и выбран"
        />
        <p className="text-xs text-muted-foreground">
          Тег, который будет автоматически присвоен пользователю при переходе по UTM ссылке
        </p>
        {selectedTag && (
          <Badge
            variant="outline"
            style={{ borderColor: selectedTag.color, color: selectedTag.color }}
          >
            {selectedTag.name}
          </Badge>
        )}
      </div>

      <div className="space-y-2">
        <Label>Переход после открытия</Label>
        <LinkTargetSelect
          value={formData.redirectUrl}
          onChange={(url) => handleChange('redirectUrl', url)}
          typeLabel="Куда вести"
        />
        <p className="text-xs text-muted-foreground">
          После регистрации/авторизации пользователь будет автоматически переведён
          на выбранную страницу или сущность. Пусто — без перехода.
        </p>
      </div>
    </div>
  );

  return (
    <div className={cn('space-y-6', isPageLayout && 'max-w-lg')}>
      {isPageLayout && (
        <FormHeader
          title={isEdit ? 'Редактировать UTM источник' : 'Создать UTM источник'}
          description="Источники для отслеживания Telegram-трафика"
          sticky
          icon={<Link2 className="h-5 w-5" />}
          onBack={onBack}
        />
      )}

      {(savedBotLink ?? savedShortCode) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">UTM ссылка</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded border bg-muted px-3 py-2 text-xs">
                {savedBotLink ?? savedShortCode}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Скопировать UTM ссылку"
                onClick={handleCopyLink}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <form id={formId} onSubmit={handleFormSubmit} className="space-y-6">
        {isPageLayout ? (
          <Card>
            <CardHeader>
              <CardTitle>Основная информация</CardTitle>
            </CardHeader>
            <CardContent>{fields}</CardContent>
          </Card>
        ) : fields}
      </form>

      {isPageLayout ? (
        <>
          <FormSpacer />
          <FormStickyActions
            primaryLabel={isEdit ? 'Сохранить' : 'Создать'}
            secondaryLabel="Отмена"
            onPrimary={submitData}
            onSecondary={onBack}
            isPending={isSubmitting}
            pendingLabel={isEdit ? 'Сохранение...' : 'Создание...'}
          />
        </>
      ) : (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button type="submit" form={formId} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? (isEdit ? 'Сохранение...' : 'Создание...') : (isEdit ? 'Сохранить' : 'Создать')}
          </Button>
        </div>
      )}
    </div>
  );
}
